import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import campaignsDashboardSingleFiltersDropFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/campaigns_dashboard/campaigns_dashboard_single_filters_drop_form_submit';
import { createStaticDataProvider } from '@hh.ru/magritte-common-data-provider';
import { Card, Flex, RenderContentBefore, SearchInput, Text, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { useApplyCampaignsFilters } from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/useApplyCampaignsFilters';
import {
    getAppliedCampaignsFiltersTotalCount,
    MAX_CHECKBOX_SHOWN_COUNT,
    OVERLY_MASSIVE_FILTERS,
} from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/utils';
import { FilterOption, SingleFilter } from 'components/common/SingleFilter/SingleFilter';

import { CampaignFilter, setCampaignsFiltersDraft } from 'store/models/campaignsFilters';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    filters: {
        [CampaignFilter.ProductTypes]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.productTypes',
        [CampaignFilter.CampaignStatuses]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.campaignStatuses',
        [CampaignFilter.BudgetStatuses]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.budgetStatuses',
        [CampaignFilter.ModerationStatuses]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.moderationStatuses',
        [CampaignFilter.Areas]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.areas',
        [CampaignFilter.ProfessionalRoles]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.professionalRoles',
        [CampaignFilter.CreatedByUserIds]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.createdByUserIds',
    },
    apply: 'CampaignsFiltersPanel.AllFiltersModal.applyButton',
    discard: 'CampaignsFiltersPanel.AllFiltersModal.resetButton',
    filterSearchPlaceholder: 'CampaignsFiltersPanel.AllFiltersModal.filterSearchPlaceholder',
    emptyOptionsPlaceholder: 'CampaignsFiltersPanel.CampaignSingleFilter.emptyOptionsPlaceholder',
    emptyOptionsPlaceholderText: 'CampaignsFiltersPanel.CampaignSingleFilter.emptyOptionsPlaceholderText',
};

interface CampaignSingleFilterProps {
    filterName: CampaignFilter;
    filterList: Record<string, string>;
}

interface FilterSearchContentProps {
    filterName: CampaignFilter;
    searchValue: string;
    optionsCount: number;
    setSearchValue: (value: string) => void;
    onEmptyOptionsChange: (isEmpty: boolean) => void;
    filterSearchPlaceholder: string;
    emptyOptionsPlaceholder: string;
    emptyOptionsPlaceholderText: string;
}

const FilterSearchContent: FC<FilterSearchContentProps> = ({
    filterName,
    searchValue,
    optionsCount,
    setSearchValue,
    onEmptyOptionsChange,
    filterSearchPlaceholder,
    emptyOptionsPlaceholder,
    emptyOptionsPlaceholderText,
}) => {
    useEffect(() => {
        onEmptyOptionsChange(!optionsCount);
    }, [optionsCount, onEmptyOptionsChange]);

    return (
        <>
            <SearchInput
                clearable
                data-qa={`filter-search-${filterName}`}
                onChange={setSearchValue}
                placeholder={filterSearchPlaceholder}
                size="large"
                value={searchValue}
            />
            {searchValue && !optionsCount && (
                <Card paddingLeft={8} paddingRight={8} paddingTop={16} paddingBottom={16}>
                    <Flex direction="column" align="center">
                        <Text typography="title-5-semibold">{emptyOptionsPlaceholder}</Text>
                        <VSpacing default={8} />
                        <Text typography="paragraph-2-regular" style="secondary">
                            {emptyOptionsPlaceholderText}
                        </Text>
                    </Flex>
                </Card>
            )}
        </>
    );
};

export const CampaignSingleFilter: FC<CampaignSingleFilterProps> = ({ filterName, filterList }) => {
    const appliedFilter = useSelector((state) => state.campaignsFilters.appliedCampaignsFilters[filterName]);
    const appliedCampaignsFilters = useSelector((state) => state.campaignsFilters.appliedCampaignsFilters);
    const campaignsFiltersDraft = useSelector((state) => state.campaignsFilters.campaignsFiltersDraft);
    const isCampaignsLoading = useSelector((state) => state.campaignsDashboard.isCampaignsLoading);
    const [isEmptyOptions, setIsEmptyOptions] = useState(false);

    const filterDraft = campaignsFiltersDraft[filterName];

    const trls = useTrls();
    const dispatch = useDispatch();
    const { applyCampaignsFilters } = useApplyCampaignsFilters();

    const options = useMemo<FilterOption[]>(
        () => Object.entries(filterList).map(([value, text]) => ({ value, text })),
        [filterList]
    );
    const dataProvider = useMemo(() => createStaticDataProvider(options), [options]);

    const appliedValues = useMemo(
        () => options.filter((option) => appliedFilter.has(option.value)),
        [options, appliedFilter]
    );
    const value = useMemo(() => options.filter((option) => filterDraft.has(option.value)), [options, filterDraft]);

    const onChangeFilterDraftHandler = (values: FilterOption[]) => {
        dispatch(
            setCampaignsFiltersDraft({
                ...campaignsFiltersDraft,
                [filterName]: new Set(values.map((option) => option.value)),
            })
        );
    };

    const applyFilter = (selectedValues: Set<string>) => {
        const newAppliedFilters = { ...appliedCampaignsFilters, [filterName]: selectedValues };

        const selectedValuesAsString = [...selectedValues].toString();

        applyCampaignsFilters({
            filters: newAppliedFilters,
            count: getAppliedCampaignsFiltersTotalCount(newAppliedFilters),
            onSuccess: () => {
                campaignsDashboardSingleFiltersDropFormSubmit({ filterName, options: selectedValuesAsString });
            },
            onError: (error) => {
                campaignsDashboardSingleFiltersDropFormSubmit({
                    filterName,
                    options: selectedValuesAsString,
                    error: JSON.stringify(error),
                });
            },
        });
    };

    const onApplyHandler = () => {
        if (appliedFilter.size === filterDraft.size && [...filterDraft].every((value) => appliedFilter.has(value))) {
            return;
        }

        applyFilter(new Set(filterDraft));
    };

    const onClearHandler = () => {
        dispatch(
            setCampaignsFiltersDraft({
                ...campaignsFiltersDraft,
                [filterName]: new Set<string>(),
            })
        );
        applyFilter(new Set<string>());
    };

    const dropCloseHandler = () => {
        setIsEmptyOptions(false);
        dispatch(
            setCampaignsFiltersDraft({
                ...campaignsFiltersDraft,
                [filterName]: new Set(appliedFilter),
            })
        );
    };

    const onEmptyOptionsChange = useCallback((isEmpty: boolean) => {
        setIsEmptyOptions(isEmpty);
    }, []);

    const renderContentBefore: RenderContentBefore = useCallback(
        ({ setSearchValue, searchValue, optionsCount }) => (
            <FilterSearchContent
                filterName={filterName}
                searchValue={searchValue}
                optionsCount={optionsCount}
                setSearchValue={setSearchValue}
                onEmptyOptionsChange={onEmptyOptionsChange}
                filterSearchPlaceholder={trls(TrlKeys.filterSearchPlaceholder)}
                emptyOptionsPlaceholder={trls(TrlKeys.emptyOptionsPlaceholder)}
                emptyOptionsPlaceholderText={trls(TrlKeys.emptyOptionsPlaceholderText)}
            />
        ),
        [filterName, onEmptyOptionsChange, trls]
    );

    return (
        <SingleFilter
            name={filterName}
            placeholder={trls(TrlKeys.filters[filterName])}
            appliedValues={appliedValues}
            value={value}
            isLoading={isCampaignsLoading}
            dataProvider={dataProvider}
            applyLabel={trls(TrlKeys.apply)}
            discardLabel={trls(TrlKeys.discard)}
            dataQaPrefix={`campaigns-single-filter-${filterName}`}
            dropWidth={360}
            forceDropVisibleWithEmptyOptions
            showButtons={!isEmptyOptions}
            renderContentBefore={
                OVERLY_MASSIVE_FILTERS.includes(filterName) && options.length >= MAX_CHECKBOX_SHOWN_COUNT
                    ? renderContentBefore
                    : undefined
            }
            onApply={onApplyHandler}
            onClear={onClearHandler}
            onChange={onChangeFilterDraftHandler}
            onSelectOption={onChangeFilterDraftHandler}
            onDropClose={dropCloseHandler}
            onBottomSheetClose={dropCloseHandler}
        />
    );
};
